import Image from 'next/image'
import { useTranslation } from 'next-i18next'
import {
  Box,
  Heading,
  Stack,
  Text,
  TextLink,
  useResponsiveValue
} from '@machobear/design-system'

import globeIcon from '@/features/Company/icons/globe-icon.png'
import markerIcon from '@/features/Company/icons/marker-icon.png'
import Head from '@/components/head'

import { video } from './index.css'
import titleLogo from './logo.png'

type Props = {
  metadata?: {
    __typename: string
    page: number
    page_size: number
    total_count: number
  }
}
const RealstarProfile = ({ metadata }: Props) => {
  const { t } = useTranslation('realstarcorp')
  const profile = {
    title: t('realstarcorp:Realstar Corp'),
    url: '/company/realstarcorp'
  }

  const isMobile = useResponsiveValue()({
    mobile: true,
    tablet: false
  })

  const isDesktop = useResponsiveValue()({
    mobile: false,
    tablet: false,
    desktop: true
  })

  return (
    <>
      <Head
        title={`${profile.title} | liv.rent`}
        path={profile.url}
        pageData={metadata}
        hideCustomerSupport
      />
      <Box
        style={{ height: isMobile ? '210px' : isDesktop ? '575px' : '350px' }}
        marginBottom="gutter"
        overflow="hidden"
        position="relative"
      >
        <Box
          display="flex"
          justifyContent="center"
          alignItems="center"
          height="full"
          width="full"
          overflow="hidden"
        >
          <video autoPlay loop muted playsInline className={video}>
            <source
              src="/videos/realstarcorp-banner-mobile.mp4"
              type="video/mp4"
              media="(max-width: 599px)"
            />
            <source
              src="/videos/realstarcorp-banner.mp4"
              type="video/mp4"
              media="(min-width: 600px)"
            />
          </video>
        </Box>
      </Box>
      <Box
        display="flex"
        justifyContent="center"
        width="full"
        marginBottom="large"
      >
        <Stack space="medium">
          <Box display="flex" alignItems="center" marginRight="large">
            <Box
              marginRight="xsmall"
              marginLeft="medium"
              style={{ flexShrink: 0, marginTop: 10 }}
            >
              <Image
                src={titleLogo.src}
                width={120}
                height={40}
                alt="Realstar"
              />
            </Box>
            <Heading level="3" weight="weak">
              {t('realstarcorp:Realstar Corp.')}
            </Heading>
          </Box>
          <Box
            display="flex"
            flexDirection={{ mobile: 'column', tablet: 'row' }}
            gap={{ mobile: 'gutter', tablet: 'none' }}
            marginX="medium"
            marginBottom="xlarge"
            marginTop="medium"
            style={{
              maxWidth: '1096px'
            }}
          >
            <Box
              display="flex"
              flexDirection="column"
              marginRight="xxlarge"
              style={{
                width: isMobile ? '100%' : 'fit-content'
              }}
            >
              <Stack space="medium">
                <Stack space="gutter">
                  <Text size="standard" weight="strong">
                    {t(`realstarcorp:subtitle 1`)}
                  </Text>
                  <Text size="standard" weight="strong">
                    {t(`realstarcorp:subtitle 2`)}
                  </Text>
                  <Text size="standard" weight="strong">
                    {t(`realstarcorp:subtitle 3`)}
                  </Text>
                  <Text size="standard" weight="strong">
                    {t(`realstarcorp:subtitle 4`)}
                  </Text>
                  <Text size="standard" weight="strong">
                    {t(`realstarcorp:subtitle 5`)}
                  </Text>

                  <Text size="standard">{t('realstarcorp:description')}</Text>
                </Stack>
              </Stack>
            </Box>
            <Box
              background="neutralSoft"
              borderRadius="small"
              paddingY="small"
              paddingX="gutter"
              marginTop={{ mobile: 'gutter', tablet: 'none' }}
              style={{
                height: 'min-content',
                width: isMobile ? '100%' : 'fit-content',
                minWidth: '300px'
              }}
            >
              <Stack space="medium">
                <Box display="flex" alignItems="center" width="full">
                  <Box
                    marginRight="xxsmall"
                    style={{ width: '16px', flexShrink: 0 }}
                  >
                    <Image src={globeIcon.src} alt="" width={16} height={16} />
                  </Box>
                  <TextLink
                    href="https://www.realstar.ca/searchlisting"
                    rel="noreferrer noopener"
                    target="_blank"
                  >
                    www.realstar.ca
                  </TextLink>
                </Box>
                <Box display="flex" alignItems="center" width="full">
                  <Box
                    marginRight="xxsmall"
                    style={{ width: '16px', flexShrink: 0 }}
                  >
                    <Image src={markerIcon.src} alt="" width={16} height={16} />
                  </Box>
                  <TextLink
                    href={`https://www.realstar.ca/contactus`}
                    rel="noreferrer noopener"
                    target="_blank"
                  >
                    Contact us
                  </TextLink>
                </Box>
              </Stack>
            </Box>
          </Box>
        </Stack>
      </Box>
      <Box textAlign="center" paddingBottom="gutter">
        <Heading level="6">{t('realstarcorp:Our Portfolio')}</Heading>
      </Box>
    </>
  )
}

export default RealstarProfile
